import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, MapPin, CheckCircle, Calendar, DollarSign, ChevronDown, ChevronUp, Star, Navigation } from 'lucide-react';
import axios from 'axios';
import { DateTimePicker } from '../components/ui/DateTimePicker';
import LocationMap from '../components/LocationMap';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// ── Status progress bar ───────────────────────────────────────────────────────
const STATUS_STAGES = ['request', 'accepted', 'payment', 'in_progress', 'confirmation', 'complete'];

const stageForStatus = (status) => {
  if (['pending', 'negotiating'].includes(status)) return 'request';
  if (status === 'provider_accepted') return 'accepted';
  if (status === 'payment_pending') return 'payment';
  if (status === 'in_progress') return 'in_progress';
  if (status === 'awaiting_confirmation') return 'confirmation';
  if (status === 'completed') return 'complete';
  return null; // cancelled / disputed — handled separately
};

const StatusBar = ({ status, t }) => {
  if (status === 'cancelled') {
    return (
      <div className="bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-sm text-gray-600 font-medium mb-4">
        {t('dashboard.cancelled')} — {t('service.status_cancelled')}
      </div>
    );
  }
  if (status === 'disputed') {
    return (
      <div className="bg-red-50 border border-red-300 rounded-lg px-4 py-2 text-sm text-red-700 font-medium mb-4">
        {t('dashboard.disputed')}
      </div>
    );
  }

  const currentStage = stageForStatus(status);
  const currentIdx = STATUS_STAGES.indexOf(currentStage);

  return (
    <div className="flex items-center gap-0 mb-6 overflow-x-auto">
      {STATUS_STAGES.map((stage, idx) => {
        const isDone = idx < currentIdx;
        const isActive = idx === currentIdx;
        return (
          <React.Fragment key={stage}>
            <div className={`flex flex-col items-center min-w-[56px] ${isActive ? 'text-primary font-semibold' : isDone ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mb-0.5
                ${isActive ? 'bg-primary text-white' : isDone ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {isDone ? '✓' : idx + 1}
              </div>
              <span className="text-[10px] text-center leading-tight">{t(`job.status_bar_${stage}`)}</span>
            </div>
            {idx < STATUS_STAGES.length - 1 && (
              <div className={`flex-1 h-0.5 mx-0.5 ${idx < currentIdx ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const ServiceDetailPage = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [acceptDialog, setAcceptDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [agreedDate, setAgreedDate] = useState('');
  const [finalAmount, setFinalAmount] = useState(0);
  const [categoryLabel, setCategoryLabel] = useState('');
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [disputeDialog, setDisputeDialog] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [gpsErrorDialog, setGpsErrorDialog] = useState(false);
  const [tooFarError, setTooFarError] = useState(null); // { distance_meters, max_meters }
  const [confirmDialog, setConfirmDialog] = useState(false);
  // Proposal form (provider)
  const [proposalPrice, setProposalPrice] = useState('');
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposalHours, setProposalHours] = useState('');
  const [proposalSubmitting, setProposalSubmitting] = useState(false);
  const [proposalSubmitted, setProposalSubmitted] = useState(false);
  const [myProposal, setMyProposal] = useState(null);

  // Rating state (Step 9c)
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  // Client rating for provider view (Step 9d)
  const [clientRating, setClientRating] = useState(null);

  const messagesEndRef = useRef(null);

  // Unread count via localStorage timestamp comparison
  const storageKey = `chat_opened_${requestId}`;
  const lastOpened = localStorage.getItem(storageKey);
  const unreadCount = messages.filter(m =>
    m.sender_id !== user?.id &&
    (!lastOpened || m.timestamp > lastOpened)
  ).length;

  const markChatRead = useCallback(() => {
    localStorage.setItem(storageKey, new Date().toISOString());
    // Also mark notifications read for this entity
    axios.post(`${API}/notifications/mark-read`, { entity_id: requestId }).catch(() => {});
  }, [storageKey, requestId]);

  const fetchRequest = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/service-requests/${requestId}/summary`);
      const data = response.data;
      setRequest(data);
      if (data.final_amount) setFinalAmount(data.final_amount);
      else if (data.estimated_amount) setFinalAmount(data.estimated_amount);
      if (data.agreed_date) setAgreedDate(data.agreed_date);

      const lang = i18n.language;
      const rn = (obj) => obj?.[lang] ?? obj?.['es'] ?? obj?.['en'] ?? '';
      const catName = rn(data.category_name);
      const subName = rn(data.subcategory_name);
      if (catName && subName) setCategoryLabel(`${catName} › ${subName}`);
      else setCategoryLabel(catName || subName);

      // Check if provider already submitted a proposal
      if (user?.role === 'provider') {
        const existing = (data.proposals || []).find(p => p.provider_id === user.id);
        if (existing) {
          setMyProposal(existing);
          setProposalSubmitted(true);
        }
      }
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [requestId, i18n.language, user]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages/${requestId}`);
      setMessages(response.data);
    } catch {
      // non-fatal
    }
  }, [requestId]);

  useEffect(() => {
    fetchRequest();
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [fetchRequest, fetchMessages]);

  // Fetch client rating when provider views a negotiating/pending job
  useEffect(() => {
    if (!request) return;
    const isProviderView = user?.role === 'provider' && user?.id === request.provider_id;
    if (isProviderView && ['negotiating', 'pending'].includes(request.status) && request.client_id) {
      axios.get(`${API}/users/${request.client_id}/client-rating`)
        .then(res => setClientRating(res.data))
        .catch(() => {});
    }
  }, [request, user]);

  const submitRating = async (e) => {
    e.preventDefault();
    if (!ratingScore) return;
    setRatingSubmitting(true);
    try {
      await axios.post(`${API}/ratings`, {
        service_request_id: requestId,
        score: ratingScore,
        comment: ratingComment || null,
      });
      // Update local state immediately — avoids form reappearing before next fetch
      setRequest(prev => prev ? { ...prev, has_rated: true } : prev);
      toast.success(t('rating.thank_you'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setRatingSubmitting(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${API}/messages`, { service_request_id: requestId, content: newMessage, location: null });
      setNewMessage('');
      markChatRead();
      await fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to send message'));
    }
  };

  const providerAcceptService = async () => {
    try {
      await axios.post(`${API}/service-requests/${requestId}/provider-accept`, null, {
        params: { agreed_date: agreedDate, final_amount: finalAmount }
      });
      toast.success('Service accepted! Waiting for client payment.');
      setAcceptDialog(false);
      await fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept service');
    }
  };

  const confirmCompletion = async () => {
    try {
      const response = await axios.post(`${API}/service-requests/${requestId}/confirm-completion`);
      toast.success(response.data.both_confirmed ? 'Service completed!' : 'Completion confirmed. Waiting for other party.');
      await fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm completion');
    }
  };

  const cancelJob = async () => {
    try {
      await axios.post(`${API}/service-requests/${requestId}/cancel`);
      toast.success('Job cancelled.');
      setCancelDialog(false);
      await fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const disputeJob = async () => {
    try {
      await axios.post(`${API}/service-requests/${requestId}/dispute`);
      toast.success('Dispute opened. An admin will review.');
      setDisputeDialog(false);
      await fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open dispute');
    }
  };

  const doCheckin = async (providerLat, providerLng) => {
    setTooFarError(null);
    try {
      const body = (providerLat != null && providerLng != null)
        ? { provider_lat: providerLat, provider_lng: providerLng }
        : undefined;
      await axios.post(`${API}/service-requests/${requestId}/checkin`, body);
      toast.success(t('job.checkin_button') + ' — ' + 'Service is now in progress.');
      setGpsErrorDialog(false);
      await fetchRequest();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === 'too_far_from_location') {
        setTooFarError({ distance_meters: detail.distance_meters, max_meters: detail.max_meters });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Check-in failed');
      }
    } finally {
      setCheckinLoading(false);
    }
  };

  const startCheckin = () => {
    setTooFarError(null);
    setCheckinLoading(true);
    if (!navigator.geolocation) {
      setCheckinLoading(false);
      setGpsErrorDialog(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => doCheckin(pos.coords.latitude, pos.coords.longitude),
      () => { setCheckinLoading(false); setGpsErrorDialog(true); },
      { timeout: 10000 },
    );
  };

  const submitProposal = async (e) => {
    e.preventDefault();
    setProposalSubmitting(true);
    try {
      await axios.post(`${API}/service-requests/${requestId}/proposals`, {
        price: parseFloat(proposalPrice),
        message: proposalMessage,
        estimated_hours: proposalHours ? parseFloat(proposalHours) : null,
      });
      toast.success(t('job.proposal_submitted'));
      setProposalSubmitted(true);
      await fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit proposal');
    } finally {
      setProposalSubmitting(false);
    }
  };

  const acceptProposal = async (providerIdArg) => {
    try {
      await axios.post(`${API}/service-requests/${requestId}/accept-proposal`, null, {
        params: { proposal_provider_id: providerIdArg }
      });
      toast.success('Proposal accepted!');
      await fetchRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept proposal');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-gray-100 text-gray-800',
      negotiating: 'bg-blue-100 text-blue-800',
      provider_accepted: 'bg-green-100 text-green-800',
      payment_pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      in_progress: 'bg-purple-100 text-purple-800',
      awaiting_confirmation: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-600',
      disputed: 'bg-red-100 text-red-700',
    };
    return <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>{t(`service.status_${status}`)}</Badge>;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  if (fetchError || !request) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">{fetchError ? 'Failed to load request.' : 'Request not found.'}</p>
        {fetchError && (
          <Button variant="outline" onClick={() => { setFetchError(false); setLoading(true); fetchRequest(); }}>Retry</Button>
        )}
      </div>
    );
  }

  // isProvider: assigned to this job (used for job-specific actions like accept, check-in, confirm)
  const isProvider = user?.id === request.provider_id;
  // isProviderRole: any provider user (used for proposal submission on pending/open requests)
  const isProviderRole = user?.role === 'provider';
  const isClient = user?.id === request.client_id;
  const canCancelOrDispute = !['completed', 'cancelled', 'disputed'].includes(request.status);

  return (
    <>
    <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Status bar */}
        <StatusBar status={request.status} t={t} />

        {/* Job details card */}
        <Card className="mb-6" data-testid="service-detail">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                {categoryLabel && <p className="text-xs font-medium text-muted-foreground mb-1">{categoryLabel}</p>}
                <CardTitle className="text-2xl mb-2">
                  {request.title?.[i18n.language] || request.title?.es || ''}
                </CardTitle>
                {getStatusBadge(request.status)}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>{request.description?.[i18n.language] || request.description?.es || ''}</p>
            {request.agreed_date && (
              <div className="flex items-center gap-2 text-sm bg-blue-50 p-3 rounded">
                <Calendar className="w-4 h-4" />
                <span><strong>{t('service.agreed_date')}:</strong> {new Date(request.agreed_date).toLocaleString(i18n.language)}</span>
              </div>
            )}
            {request.final_amount && (
              <div className="flex items-center gap-2 text-sm bg-green-50 p-3 rounded">
                <DollarSign className="w-4 h-4" />
                <span><strong>Amount:</strong> ${request.final_amount}</span>
              </div>
            )}
            {(request.location?.address || request.address) && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4" />
                {request.location?.address || request.address}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Embedded map + navigation links */}
        {request.location?.lat && request.location?.lng && (() => {
          const lat = request.location.lat;
          const lng = request.location.lng;
          const q   = `${lat},${lng}`;
          const showNav = isProvider && ['payment_pending', 'in_progress', 'awaiting_confirmation', 'completed'].includes(request.status);
          return (
            <Card className="mb-6 overflow-hidden">
              <LocationMap lat={lat} lng={lng} />
              <CardContent className="py-3 space-y-2">
                <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{request.location.address || q}</span>
                </div>
                {showNav && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <p className="w-full text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Navigation className="w-3.5 h-3.5" />{t('location.navigate_to')}
                    </p>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${q}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent transition-colors">
                      {t('location.open_google_maps')}
                    </a>
                    <a href={`https://waze.com/ul?ll=${q}&navigate=yes`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent transition-colors">
                      {t('location.open_waze')}
                    </a>
                    <a href={`https://maps.apple.com/?daddr=${q}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent transition-colors">
                      {t('location.open_apple_maps')}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Flow B — Provider proposal submission */}
        {isProviderRole && request.status === 'pending' && (
          <Card className="mb-6">
            <CardHeader><CardTitle>{t('job.submit_proposal')}</CardTitle></CardHeader>
            <CardContent>
              {(user?.verification_tier ?? 1) < 2 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-amber-800">{t('provider.verify_to_propose')}</p>
                  <Button size="sm" variant="outline" onClick={() => navigate('/provider/verification')}>
                    {t('provider.verify_now')}
                  </Button>
                </div>
              ) : proposalSubmitted ? (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-green-800">✓ {t('job.proposal_submitted')}</p>
                  {myProposal && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      <p>Price: ${myProposal.price}</p>
                      {myProposal.estimated_hours && <p>Est. hours: {myProposal.estimated_hours}</p>}
                      <p className="mt-1">{myProposal.message}</p>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={submitProposal} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price (USD) *</Label>
                      <Input type="number" step="0.01" min="1" value={proposalPrice}
                        onChange={e => setProposalPrice(e.target.value)} required />
                    </div>
                    <div>
                      <Label>Estimated hours (optional)</Label>
                      <Input type="number" step="0.5" min="0" value={proposalHours}
                        onChange={e => setProposalHours(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label>Message * <span className="text-xs text-muted-foreground">({proposalMessage.length}/500)</span></Label>
                    <Textarea rows={3} maxLength={500} value={proposalMessage}
                      onChange={e => setProposalMessage(e.target.value)}
                      placeholder="Describe your approach and experience..." required />
                  </div>
                  <Button type="submit" disabled={proposalSubmitting || !proposalPrice || !proposalMessage}>
                    {proposalSubmitting ? 'Submitting...' : t('job.submit_proposal')}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}


        {/* Flow B — Client sees proposals */}
        {isClient && request.status === 'pending' && (request.proposals || []).length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('job.proposals_received')} ({request.proposals.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {request.proposals.map((proposal, i) => (
                <div key={proposal.id || i} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">{proposal.provider_name}</p>
                    <p className="text-lg font-bold text-green-700">${proposal.price ?? proposal.total_amount}</p>
                  </div>
                  {proposal.estimated_hours && (
                    <p className="text-sm text-muted-foreground">Est. {proposal.estimated_hours} hours</p>
                  )}
                  <p className="text-sm">{proposal.message || proposal.notes}</p>
                  <p className="text-xs text-muted-foreground">
                    {proposal.submitted_at ? new Date(proposal.submitted_at).toLocaleDateString(i18n.language) : ''}
                  </p>
                  <Button size="sm" onClick={() => acceptProposal(proposal.provider_id)}
                    style={{ background: 'hsl(120 100% 30%)', color: 'white' }}>
                    {t('job.accept_proposal')}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Step 9c — Rating prompt (has_rated comes from API, persists across refreshes) */}
        {request.status === 'completed' && !request.has_rated && (
          <Card className="mb-6 border-yellow-200 bg-yellow-50">
            <CardHeader><CardTitle className="text-base flex items-center gap-2">
              <Star className="w-4 h-4 text-yellow-500" />{t('rating.rate_experience')}
            </CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submitRating} className="space-y-3">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => setRatingScore(n)}>
                      <Star className={`w-7 h-7 ${n <= ratingScore ? 'fill-yellow-400 stroke-yellow-400' : 'stroke-gray-300'}`} />
                    </button>
                  ))}
                </div>
                <Textarea rows={2} maxLength={300} value={ratingComment}
                  onChange={e => setRatingComment(e.target.value)}
                  placeholder={t('rating.comment_placeholder')} />
                <Button type="submit" size="sm" disabled={!ratingScore || ratingSubmitting}>
                  {ratingSubmitting ? '…' : t('rating.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
        {request.status === 'completed' && request.has_rated && (
          <div className="mb-6 text-sm text-green-700 font-medium px-1">{t('rating.thank_you')}</div>
        )}

        {/* Step 9d — Client trust badge for provider view */}
        {user?.role === 'provider' && clientRating && ['negotiating', 'pending'].includes(request.status) && (
          <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <span>Client:</span>
            {[1,2,3,4,5].map(n => (
              <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(clientRating.avg_rating) ? 'fill-yellow-400 stroke-yellow-400' : 'stroke-gray-300'}`} />
            ))}
            <span>{clientRating.avg_rating ? clientRating.avg_rating.toFixed(1) : '—'}</span>
            {clientRating.trust_level >= 3 && (
              <Badge className="bg-green-100 text-green-700 text-xs">{t('trust.trusted_client')}</Badge>
            )}
            {clientRating.trust_level === 2 && (
              <Badge className="bg-blue-100 text-blue-700 text-xs">{t('trust.payment_verified')}</Badge>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Chat */}
          <Card className="h-[500px] flex flex-col" data-testid="chat-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {t('chat.title')}
                {unreadCount > 0 && (
                  <span className="text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                    {t('job.new_messages', { count: unreadCount })}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0">
              <div className="flex-1 overflow-y-auto px-4 space-y-3" onScroll={markChatRead}>
                {messages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-lg px-4 py-2 ${msg.sender_id === user?.id ? 'bg-primary text-white' : 'bg-gray-100'}`}>
                      <div className="text-xs opacity-75 mb-1">{msg.sender_name}</div>
                      <div className="text-sm">{msg.content}</div>
                      {msg.location && (
                        <div className="flex items-center gap-1 text-xs mt-1">
                          <MapPin className="w-3 h-3" />Location shared
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={sendMessage} className="p-4 border-t">
                <div className="flex gap-2">
                  <Input value={newMessage} onChange={e => setNewMessage(e.target.value)}
                    placeholder={t('chat.type_message')} data-testid="message-input"
                    onFocus={markChatRead} />
                  <Button type="submit" size="icon" data-testid="send-button"><Send className="w-4 h-4" /></Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader><CardTitle>{t('service.actions_title')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {request.status === 'pending' && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-1">{t('service.status_pending_title')}</p>
                  <p className="text-sm text-muted-foreground">{t('service.status_pending_desc')}</p>
                  {isClient && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {(request.proposals?.length || 0) > 0
                        ? `${request.proposals.length} ${t('service.proposals_received')}`
                        : t('service.waiting_for_proposals')}
                    </p>
                  )}
                </div>
              )}

              {request.status === 'negotiating' && isClient && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-1">{t('service.status_negotiating_title')}</p>
                  <p className="text-sm text-muted-foreground">{t('service.status_negotiating_desc_client')}</p>
                </div>
              )}

              {isProvider && request.status === 'negotiating' && (
                <>
                  <Button onClick={() => setAcceptDialog(true)} className="w-full" data-testid="provider-accept-button">
                    <CheckCircle className="w-4 h-4 mr-2" />{t('service.provider_accept')}
                  </Button>
                  <Dialog open={acceptDialog} onOpenChange={setAcceptDialog}>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{t('service.provider_accept')}</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>{t('service.agreed_date')}</Label>
                          <DateTimePicker
                            value={agreedDate}
                            onChange={setAgreedDate}
                            placeholder={t('datetime.no_date')}
                          />
                        </div>
                        <div>
                          <Label>Final Amount (USD)</Label>
                          <Input type="number" step="0.01" value={finalAmount} onChange={e => setFinalAmount(parseFloat(e.target.value))} required />
                        </div>
                        <Button onClick={providerAcceptService} className="w-full" disabled={!agreedDate || !finalAmount}>
                          Confirm Acceptance
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              )}

              {isClient && request.status === 'provider_accepted' && (
                <>
                  <div className="bg-green-50 p-4 rounded-lg space-y-2">
                    <p className="text-sm font-semibold">Provider has accepted!</p>
                    <p className="text-sm"><strong>Date:</strong> {new Date(request.agreed_date).toLocaleString(i18n.language)}</p>
                    <p className="text-sm"><strong>Amount:</strong> ${request.final_amount}</p>
                  </div>
                  <Button onClick={() => setPaymentDialog(true)} className="w-full" data-testid="pay-button">
                    <DollarSign className="w-4 h-4 mr-2" />Pay ${request.final_amount} to Book
                  </Button>
                  <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>Payment - ${request.final_amount}</DialogTitle></DialogHeader>
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        const fd = new FormData(e.target);
                        try {
                          const res = await axios.post(`${API}/payments/authorize`, {
                            service_request_id: requestId,
                            card_number: fd.get('cardNumber'), cvv: fd.get('cvv'),
                            exp_month: parseInt(fd.get('expMonth')), exp_year: parseInt(fd.get('expYear')),
                            cardholder_name: fd.get('name'), cardholder_lastname: fd.get('lastname'),
                            cardholder_email: fd.get('email'), cardholder_phone: fd.get('phone'),
                            cardholder_address: fd.get('address'), cardholder_city: fd.get('city'),
                            cardholder_country: 'SV', cardholder_region: fd.get('region'),
                            cardholder_postal_code: fd.get('postalCode')
                          });
                          if (res.data.redirect_url) { window.location.href = res.data.redirect_url; }
                          else { toast.success('Payment processed!'); setPaymentDialog(false); await fetchRequest(); }
                        } catch (err) { toast.error(err.response?.data?.detail || 'Payment failed'); }
                      }}>
                        <div className="space-y-4">
                          <div className="bg-blue-50 p-3 rounded"><p className="text-sm font-semibold">Amount: ${request.final_amount} USD</p></div>
                          <div className="border-t pt-4">
                            <h3 className="font-semibold mb-3">Card Information</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="col-span-2"><Label>Card Number</Label><Input name="cardNumber" placeholder="1234 5678 9012 3456" required /></div>
                              <div><Label>Exp Month</Label><Input name="expMonth" type="number" placeholder="12" min="1" max="12" required /></div>
                              <div><Label>Exp Year</Label><Input name="expYear" type="text" placeholder="25" maxLength="2" pattern="[0-9]{2}" required /></div>
                              <div className="col-span-2"><Label>CVV</Label><Input name="cvv" type="text" placeholder="123" maxLength="4" required /></div>
                            </div>
                          </div>
                          <div className="border-t pt-4">
                            <h3 className="font-semibold mb-3">Cardholder Information</h3>
                            <div className="grid grid-cols-2 gap-4">
                              <div><Label>First Name</Label><Input name="name" required /></div>
                              <div><Label>Last Name</Label><Input name="lastname" required /></div>
                              <div className="col-span-2"><Label>Email</Label><Input name="email" type="email" required /></div>
                              <div className="col-span-2"><Label>Phone</Label><Input name="phone" type="tel" required /></div>
                              <div className="col-span-2"><Label>Address</Label><Input name="address" required /></div>
                              <div><Label>City</Label><Input name="city" required /></div>
                              <div><Label>Region</Label><Input name="region" required /></div>
                              <div className="col-span-2"><Label>Postal Code</Label><Input name="postalCode" required /></div>
                            </div>
                          </div>
                          <Button type="submit" className="w-full" style={{ background: 'hsl(120 100% 30%)' }}>
                            Pay ${request.final_amount} USD
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </>
              )}

              {/* Check In — provider only, when payment_pending */}
              {isProvider && request.status === 'payment_pending' && (
                <div className="space-y-2">
                  <Button
                    onClick={startCheckin}
                    disabled={checkinLoading}
                    className="w-full"
                    data-testid="checkin-button"
                    style={{ background: 'hsl(120 100% 30%)' }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {checkinLoading ? t('checkin.getting_location') : t('job.checkin_button')}
                  </Button>
                  {tooFarError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800">
                      {t('checkin.too_far', { distance: tooFarError.distance_meters })}
                    </div>
                  )}
                </div>
              )}

              {/* Client waiting message — payment_pending */}
              {isClient && request.status === 'payment_pending' && (
                <div className="bg-blue-50 p-4 rounded-lg text-sm">
                  <p className="font-medium">Payment received.</p>
                  <p className="text-muted-foreground mt-1">Waiting for the provider to check in at the job location.</p>
                </div>
              )}

              {request.status === 'paid' && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-green-800">✓ Service Booked & Paid</p>
                  {request.agreed_date && <p className="text-sm mt-2">Date: {new Date(request.agreed_date).toLocaleString(i18n.language)}</p>}
                </div>
              )}

              {/* Confirm completion — provider (in_progress or awaiting_confirmation) */}
              {isProvider && ['in_progress', 'awaiting_confirmation'].includes(request.status) && (
                <Button onClick={() => setConfirmDialog(true)} className="w-full" data-testid="confirm-completion-button"
                  disabled={request.provider_confirmed_completion}
                  style={!request.provider_confirmed_completion ? { background: 'hsl(120 100% 30%)' } : {}}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {request.provider_confirmed_completion ? 'You Confirmed ✓' : t('job.confirm_completion_button')}
                </Button>
              )}

              {/* Confirm completion — client (awaiting_confirmation or in_progress) */}
              {isClient && ['awaiting_confirmation', 'in_progress'].includes(request.status) && (
                <Button onClick={() => setConfirmDialog(true)} className="w-full" data-testid="confirm-completion-button-client"
                  disabled={request.client_confirmed_completion}
                  style={!request.client_confirmed_completion ? { background: 'hsl(120 100% 30%)' } : {}}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {request.client_confirmed_completion ? 'You Confirmed ✓' : t('job.confirm_completion_button')}
                </Button>
              )}

              {request.status === 'awaiting_confirmation' && (
                <div className="bg-orange-50 p-4 rounded-lg text-sm">
                  {isClient && !request.client_confirmed_completion && 'Provider confirmed. Please confirm completion.'}
                  {isProvider && !request.provider_confirmed_completion && 'Client confirmed. Please confirm completion.'}
                  {isClient && request.client_confirmed_completion && 'Waiting for provider confirmation...'}
                  {isProvider && request.provider_confirmed_completion && 'Waiting for client confirmation...'}
                </div>
              )}

              {request.status === 'completed' && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-green-800">✓ Service Completed</p>
                  <p className="text-sm mt-2">Both parties confirmed</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Options (cancel / dispute) */}
        {canCancelOrDispute && (isClient || isProvider) && (
          <Card className="mt-6">
            <CardHeader>
              <button
                className="flex items-center justify-between w-full text-left"
                onClick={() => setOptionsOpen(o => !o)}
              >
                <CardTitle className="text-base">Options</CardTitle>
                {optionsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </CardHeader>
            {optionsOpen && (
              <CardContent className="flex gap-3 flex-wrap">
                <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => setCancelDialog(true)}>
                  {t('job.cancel_job')}
                </Button>
                <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  onClick={() => setDisputeDialog(true)}>
                  {t('job.report_issue')}
                </Button>
              </CardContent>
            )}
          </Card>
        )}
      </div>

      {/* GPS unavailable — ask to proceed without validation */}
      <Dialog open={gpsErrorDialog} onOpenChange={setGpsErrorDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('checkin.gps_error_title')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('checkin.gps_error_body')}</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setGpsErrorDialog(false)} className="flex-1">Cancel</Button>
            <Button onClick={() => doCheckin(null, null)} className="flex-1"
              style={{ background: 'hsl(120 100% 30%)', color: 'white' }}>
              {t('job.checkin_button')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm completion dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('job.confirm_completion_button')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('job.confirm_work_complete')}</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setConfirmDialog(false)} className="flex-1">Cancel</Button>
            <Button onClick={async () => { await confirmCompletion(); setConfirmDialog(false); }} className="flex-1"
              style={{ background: 'hsl(120 100% 30%)', color: 'white' }}>Confirm</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('job.cancel_job')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('job.cancel_confirm')}</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setCancelDialog(false)} className="flex-1">No</Button>
            <Button onClick={cancelJob} className="flex-1 bg-red-600 hover:bg-red-700 text-white">Yes, cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dispute dialog */}
      <Dialog open={disputeDialog} onOpenChange={setDisputeDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('job.report_issue')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('job.dispute_confirm')}</p>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => setDisputeDialog(false)} className="flex-1">No</Button>
            <Button onClick={disputeJob} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white">Yes, open dispute</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ServiceDetailPage;
